import { useCallback, useMemo, useState } from 'react';
import { Grid, Typography } from 'antd';
import { WizardContainer } from '../../../components/wizard/Wizard';
import { WizardProvider } from '../../../components/wizard/useWizard';
import { AvailabilityAfterProgramViewAnswersStep } from './AvailabilityAfterProgramViewAnswersStep';
import { BasicEligibilityViewAnswersStep } from './BasicEligibilityViewAnswersStep';
import { CharacterAndHabitsViewAnswersStep } from './CharacterAndHabitsViewAnswersStep';
import { DietaryHabitsAndHealthViewAnswersStep } from './DietaryHabitsAndHealthViewAnswersStep';
import { InspirationAndWillingnessViewAnswersStep } from './InspirationAndWillingnessViewAnswersStep';
import { PracticeViewAnswersStep } from './PracticeViewAnswersStep';
import { SupportViewAnswersStep } from './SupportViewAnswersStep';
import { UnderstandingOfHeartfulnessViewAnswersStep } from './UnderstandingOfHeartfulnessViewAnswersStep';
import {
	ViewApplicantAnswersProvider,
	useViewApplicantAnswers,
} from './useViewApplicantAnswers';
import { useTranslation } from 'react-i18next';
import { useInterviewLandingPage } from '../InterviewLandingPage/useInterviewLandingPage';
import { INTERVIEW_LANDING } from '../../../shared/Routes';
import Icon from '../../../components/ui-components/Icon';
import LeftArrow from '../../../assets/images/arrow-left.svg';

const { Text } = Typography;
const { useBreakpoint } = Grid;

export const ViewApplicantAnswersWizard = () => {
	const { onSubmitClick } = useViewApplicantAnswers();
	const { navigateToPage } = useInterviewLandingPage();
	const { t } = useTranslation();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const currentWizard = 0;
	const stepOffset = 0;
	const wizards = useMemo(
		() => [
			{
				steps: [
					<BasicEligibilityViewAnswersStep key={1} />,
					<InspirationAndWillingnessViewAnswersStep key={2} />,
					<PracticeViewAnswersStep key={3} />,
					<CharacterAndHabitsViewAnswersStep key={4} />,
					<AvailabilityAfterProgramViewAnswersStep key={5} />,
					<SupportViewAnswersStep key={6} />,
					<UnderstandingOfHeartfulnessViewAnswersStep key={7} />,
					<DietaryHabitsAndHealthViewAnswersStep key={8} />,
				],
				descriptions: [
					{
						title: t('essentials'),
						description: t('thisIsMandatoryCriteria') ?? '',
					},
					{
						title: t('inspirationAndWillingness'),
						description: t('primaryQualificationForATrainer') ?? '',
					},
					{
						title: t('practice'),
						description: t('trainerNeedsToBeIntensePractitioner') ?? '',
					},
					{
						title: t('characterAndHabits'),
						description: t('expectedToBeRoleModel') ?? '',
					},
					{
						title: t('availabilityAfterProgram'),
						description: t('expectedToBeRoleModel') ?? '',
					},
					{
						title: t('familySupport'),
						description: t('exploreIfConditionsAroundAreConducive') ?? '',
					},
					{
						title: t('understandingHeartfulness'),
						description: t('abilityToExplainHeartfulness') ?? '',
					},
					{
						title: t('dietaryHabitsAndHealth'),
						description: t('expectedToBeRoleModel') ?? '',
					},
				],
			},
		],
		[t]
	);
	const steps = wizards[currentWizard].steps;
	const descriptions = wizards[currentWizard].descriptions;
	const [stepStates, setStepStates] = useState<number[]>(() => {
		const draftIndex = 0;
		return new Array(wizards.length)
			.fill(0)
			.map((_, index) =>
				index === currentWizard ? Math.max(0, draftIndex) : 0
			);
	});
	const showModal = useCallback(async (): Promise<boolean> => {
		return Promise.resolve(true);
	}, []);

	const customHandlePrevious = useCallback(() => {
		if (stepStates[currentWizard] == 0) {
			navigateToPage(INTERVIEW_LANDING);
		}
	}, [stepStates, navigateToPage]);

	const completedIndex = steps.length;

	const handleEvaluateClick = useCallback(() => {
		navigateToPage(INTERVIEW_LANDING);
	}, [navigateToPage]);

	return (
		<>
			<div
				className={`flex flex-col h-full min-h-0 p-[8px] ${
					!isMobile ? 'w-fit my-0 mx-auto' : 'w-full my-0 mx-auto'
				}`}
			>
				<div className="relative flex justify-between items-center w-full">
					<div
						className="flex font-sans justify-center gap-1 mb-1 items-center cursor-pointer absolute left-0"
						onClick={handleEvaluateClick}
					>
						<Icon src={LeftArrow} width={20} height={20} />
						{!isMobile && (
							<Text className="font-bold text-sm text-primary-blue whitespace-nowrap">
								{t('backToEvaluate')}
							</Text>
						)}
					</div>
					<Text className="flex-grow mt-4 mb-6 font-sans font-bold text-sm text-center text-secondary-black">
						{t('candidateApplication')}
					</Text>
				</div>

				<WizardContainer
					onSubmitClick={() => {
						onSubmitClick();
					}}
					onNextClick={showModal}
					onPreviousClick={customHandlePrevious}
					steps={steps}
					stepDescription={descriptions}
					stepOffset={stepOffset}
					initialStepState={stepStates[currentWizard] || 0}
					onStepStateChange={(newState: number) => {
						setStepStates((prev) => {
							const updated = [...prev];
							updated[currentWizard] = newState;
							return updated;
						});
					}}
					currentWizard={currentWizard}
					draftIndex={completedIndex}
					completedDraftIndex={completedIndex}
				/>
			</div>
		</>
	);
};

export const ViewApplicantAnswersWizardContainer = () => {
	return (
		<ViewApplicantAnswersProvider>
			<WizardProvider>
				<ViewApplicantAnswersWizard />
			</WizardProvider>
		</ViewApplicantAnswersProvider>
	);
};
